package er.prateek.crossly.ui.screens.crossword

import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.game.crossword.CrosswordEngine

/** One clue parked in the margin, pointing at the word it belongs to. */
data class GutterSlot(
    val clue: Clue,
    val direction: ClueDirection,
)

/**
 * The clues that can be shown *around* the grid instead of one-at-a-time in the
 * clue bar: a strip above the board holding each column's Down clue, and a strip
 * down the left holding each row's Across clue.
 *
 * A clue only earns a margin slot when its word starts at the edge of its
 * row/column — that is, at the first playable square scanning in from the
 * margin, so the arrow drawn beside it genuinely points along the answer. Any
 * clue that starts mid-grid (behind a black square) has no slot and stays
 * reachable the way it always was: tap its numbered square, or step to it with
 * the clue bar.
 */
class ClueGutter(
    val leftByRow: Map<Int, GutterSlot>,
    val topByCol: Map<Int, GutterSlot>,
) {
    val isEmpty: Boolean get() = leftByRow.isEmpty() && topByCol.isEmpty()
}

fun buildClueGutter(engine: CrosswordEngine): ClueGutter {
    val leftByRow = buildMap {
        for (row in 0 until engine.height) {
            val col = (0 until engine.width).firstOrNull { engine.isPlayable(row, it) } ?: continue
            engine.puzzle.cluesAcross
                .firstOrNull { it.row == row && it.col == col }
                ?.let { put(row, GutterSlot(it, ClueDirection.ACROSS)) }
        }
    }
    val topByCol = buildMap {
        for (col in 0 until engine.width) {
            val row = (0 until engine.height).firstOrNull { engine.isPlayable(it, col) } ?: continue
            engine.puzzle.cluesDown
                .firstOrNull { it.row == row && it.col == col }
                ?.let { put(col, GutterSlot(it, ClueDirection.DOWN)) }
        }
    }
    return ClueGutter(leftByRow = leftByRow, topByCol = topByCol)
}

private fun CrosswordEngine.isPlayable(row: Int, col: Int): Boolean =
    cellAt(CellPos(row, col))?.isBlack == false
